import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { SignaturePad } from "../components/SignaturePad";

const TASKS = [
  ["115", "Meal Preparation"], ["116", "Housework/Chores"], ["118", "Managing Medications"],
  ["119", "Shopping"], ["120", "Transportation"], ["122", "Hygiene"], ["123", "Dressing Upper"],
  ["124", "Dressing Lower"], ["125", "Locomotion"], ["126", "Transfer"], ["127", "Toilet Use"],
  ["128", "Bed Mobility"], ["129", "Eating"], ["130", "Bladder Incontinence"],
  ["131", "Bowel Incontinence"], ["132", "Personal Care"], ["134", "Bathing"], ["135", "Bathing (Shower)"],
];
const TIME_OFF_TYPES = ["Paid time off", "Unpaid time off", "Sick", "Personal", "Other"];
const INCIDENT_TYPES = ["On-the-job injury", "Safety hazard", "Exposure (blood/fluids)", "Client-related concern", "Vehicle / travel", "Other"];
const CLIENT_INCIDENT_CATEGORIES = ["Fall", "Abuse / neglect / exploitation", "Medication error", "Serious injury", "Death",
  "Service interruption", "Unsafe environment", "Unplanned hospitalization", "Missed visit", "Other"];

const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_PILL = {
  pending: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  submitted: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  reported: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]",
  approved: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  reviewed: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  acknowledged: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  entered: "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]",
  closed: "border-stone-300 bg-stone-100 text-stone-500",
  denied: "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]",
};
const Pill = ({ s }) => (
  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_PILL[s] || STATUS_PILL.pending}`} data-testid={`pill-${s}`}>{s}</span>
);

function TaskCodeForm({ mine, reload }) {
  const [form, setForm] = useState({ employee_name: "", reason: "", work_date: "", client_name: "", visit_note: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.employee_name ? f : { ...f, employee_name: user.name })); }, [user]);
  const [tasks, setTasks] = useState([]);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/task-code-reports", { ...form, tasks: tasks.map((t) => t.join(" - ")), signature_png: sig });
      toast.success("Task code report submitted");
      setForm((f) => ({ employee_name: f.employee_name, reason: "", work_date: "", client_name: "", visit_note: "" })); setTasks([]); setSig(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <p className="rounded-md bg-accent/60 px-3 py-2 text-xs text-stone-600" data-testid="tcr-hint">
        This form is for reporting task codes ONLY — you punched in and out but didn't enter (or need to update) task codes.
        If you did NOT punch in or out, use the <Link to="/missed-punch/new" className="font-medium text-primary hover:underline">Missed Punch form</Link> instead.
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">I am reporting task codes because *</label>
        <Input value={form.reason} onChange={set("reason")} placeholder="e.g. app closed before I could enter them" className="border-stone-300 bg-white" data-testid="tcr-reason" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Work date *</label>
          <Input type="date" value={form.work_date} onChange={set("work_date")} max={new Date().toISOString().slice(0, 10)} className="border-stone-300 bg-white" data-testid="tcr-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Client's first name only *</label>
          <Input value={form.client_name} onChange={set("client_name")} className="border-stone-300 bg-white" data-testid="tcr-client" />
        </div>
      </div>
      <div>
        <p className="mb-2 text-xs font-medium text-stone-500">Task list *</p>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {TASKS.map(([code, label]) => (
            <label key={code} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${tasks.some((t) => t[0] === code) ? "border-primary/50 bg-primary/5 text-stone-900" : "border-stone-200 text-stone-600 hover:bg-accent"}`} data-testid={`tcr-task-${code}`}>
              <input type="checkbox" checked={tasks.some((t) => t[0] === code)}
                onChange={() => setTasks((l) => (l.some((t) => t[0] === code) ? l.filter((t) => t[0] !== code) : [...l, [code, label]]))} />
              <span><span className="font-medium">{code}</span> {label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Client visit note *</label>
        <Textarea value={form.visit_note} onChange={set("visit_note")} className="min-h-20 border-stone-300 bg-white text-sm" data-testid="tcr-note" />
      </div>
      <div>
        <p className="mb-1 text-xs font-medium text-stone-500">Signature *</p>
        <SignaturePad onChange={setSig} />
      </div>
      <Button onClick={submit} disabled={busy || !form.reason.trim() || !form.work_date || !form.client_name.trim() || !tasks.length || !form.visit_note.trim() || !sig}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="tcr-submit">
        {busy ? "Submitting…" : "Submit task code report"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="tcr-mine">
          {mine.slice(0, 5).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`tcr-mine-${r.id}`}>
              <span>{r.work_date} · {r.client_name} · {(r.tasks || []).length} tasks</span>
              <Pill s={r.status} />
              {r.review_note && <span className="text-stone-400">— {r.review_note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeOffForm({ mine, reload }) {
  const [form, setForm] = useState({ employee_name: "", type: "", start_date: todayStr(), end_date: "", reason: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.employee_name ? f : { ...f, employee_name: user.name })); }, [user]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/time-off", form);
      toast.success("Time-off request sent to the office");
      setForm((f) => ({ employee_name: f.employee_name, type: "", start_date: todayStr(), end_date: "", reason: "" }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Your name *</label>
        <Input value={form.employee_name} onChange={set("employee_name")} className="border-stone-300 bg-white sm:max-w-xs" data-testid="to-name" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Type *</label>
          <select value={form.type} onChange={set("type")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="to-type">
            <option value="">Select…</option>
            {TIME_OFF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">First day off *</label>
          <Input type="date" value={form.start_date} onChange={set("start_date")} className="border-stone-300 bg-white" data-testid="to-start" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Last day off *</label>
          <Input type="date" value={form.end_date} onChange={set("end_date")} className="border-stone-300 bg-white" data-testid="to-end" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Reason / notes for the office</label>
        <Textarea value={form.reason} onChange={set("reason")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="to-reason" />
      </div>
      <Button onClick={submit} disabled={busy || !form.type || !form.start_date || !form.end_date}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="to-submit">
        {busy ? "Sending…" : "Submit request"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="to-mine">
          {mine.slice(0, 6).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`to-mine-${r.id}`}>
              <span>{r.start_date} → {r.end_date} · {r.type}</span>
              <Pill s={r.status} />
              {r.review_note && <span className="text-stone-400">— {r.review_note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmployeeIncidentForm({ mine, reload }) {
  const [form, setForm] = useState({ employee_name: "", incident_date: todayStr(), type: "", location: "", witnesses: "", description: "", action_taken: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.employee_name ? f : { ...f, employee_name: user.name })); }, [user]);
  const [reported, setReported] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/employee-incidents", { ...form, reported_to_office: reported, signature_png: sig });
      toast.success("Incident report submitted — the office will review it");
      setForm({ incident_date: "", type: "", location: "", description: "", action_taken: "" }); setReported(false); setSig(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-[#C0392B]/30 bg-[#C0392B]/5 p-3 text-xs leading-relaxed text-stone-700" data-testid="ei-verbal-first">
        <p className="font-semibold text-[#C0392B]">Before you fill this out — verbal report is required:</p>
        <ol className="mt-1 list-decimal space-y-0.5 pl-4">
          <li><b>Secure everyone's safety first</b> — if there's an emergency, call 911.</li>
          <li><b>Call the office at 267-547-0310 as soon as you are able</b> — a verbal report is required; this form does not replace it.</li>
          <li><b>Then complete this report.</b></li>
        </ol>
        <p className="mt-1.5"><b>Be objective:</b> state only facts — what you saw, what was said (exact words), times, and who was present. No opinions, guesses, or conclusions.</p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Your name *</label>
        <Input value={form.employee_name} onChange={set("employee_name")} className="border-stone-300 bg-white" data-testid="ei-name" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Date of incident *</label>
          <Input type="date" value={form.incident_date} onChange={set("incident_date")} max={new Date().toISOString().slice(0, 10)} className="border-stone-300 bg-white" data-testid="ei-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Type *</label>
          <select value={form.type} onChange={set("type")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="ei-type">
            <option value="">Select…</option>
            {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Location (client home, travel, office…)</label>
        <Input value={form.location} onChange={set("location")} className="border-stone-300 bg-white" data-testid="ei-location" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Witnesses (names, if anyone saw or heard it)</label>
        <Input value={form.witnesses} onChange={set("witnesses")} className="border-stone-300 bg-white" data-testid="ei-witnesses" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">What happened? * <span className="font-normal text-stone-400">(facts only — what you saw and heard, exact words, times)</span></label>
        <Textarea value={form.description} onChange={set("description")} className="min-h-24 border-stone-300 bg-white text-sm" data-testid="ei-description" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Immediate action taken</label>
        <Textarea value={form.action_taken} onChange={set("action_taken")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="ei-action" />
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700" data-testid="ei-reported-label">
        <input type="checkbox" checked={reported} onChange={(e) => setReported(e.target.checked)} data-testid="ei-reported" />
        I already called the office about this
      </label>
      <div>
        <p className="mb-1 text-xs font-medium text-stone-500">Signature *</p>
        <SignaturePad onChange={setSig} />
      </div>
      <Button onClick={submit} disabled={busy || !form.incident_date || !form.type || !form.description.trim() || !sig}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="ei-submit">
        {busy ? "Submitting…" : "Submit incident report"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="ei-mine">
          {mine.slice(0, 5).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`ei-mine-${r.id}`}>
              <span>{r.incident_date} · {r.type}</span>
              <Pill s={r.status} />
              {r.review_note && <span className="text-stone-400">— {r.review_note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientIncidentForm({ mine, reload }) {
  const [form, setForm] = useState({ employee_name: "", kind: "incident", category: "", date: todayStr(), time: "", client_name: "", parties_involved: "", witnesses: "", description: "", immediate_response: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.employee_name ? f : { ...f, employee_name: user.name })); }, [user]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/incident-reports", form);
      toast.success(data.serious
        ? "Filed — this is a serious incident. The office was notified immediately. If you haven't yet, call 267-547-0310 now."
        : "Report filed — remember a verbal report to the office is still required: 267-547-0310");
      setForm((f) => ({ employee_name: f.employee_name, kind: "incident", category: "", date: todayStr(), time: "", client_name: "", parties_involved: "", witnesses: "", description: "", immediate_response: "" }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-[#C0392B]/30 bg-[#C0392B]/5 p-3 text-xs leading-relaxed text-stone-700" data-testid="cir-verbal-first">
        <p className="font-semibold text-[#C0392B]">Before you fill this out — verbal report is required:</p>
        <ol className="mt-1 list-decimal space-y-0.5 pl-4">
          <li><b>Secure the client's safety first</b> — if there's an emergency, call 911.</li>
          <li><b>Call the office at 267-547-0310 as soon as you are able</b> — a verbal report is required; this form does not replace it. Suspected abuse, neglect, or exploitation: tell a supervisor immediately — you are a mandated reporter.</li>
          <li><b>Then complete this report within 24 hours of discovering the incident.</b></li>
        </ol>
        <p className="mt-1.5"><b>Be objective:</b> state only facts — what you saw, what was said (exact words), times, and who was present. No opinions, guesses, or conclusions. Only the office sees what you file here.</p>
      </div>
      <div className="flex gap-2">
        {[["incident", "Incident"], ["complaint", "Complaint"]].map(([kv, kl]) => (
          <button key={kv} onClick={() => setForm((f) => ({ ...f, kind: kv }))}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.kind === kv ? "border-primary/50 bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-400"}`}
            data-testid={`cir-kind-${kv}`}>{kl}</button>
        ))}
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Your name *</label>
        <Input value={form.employee_name} onChange={set("employee_name")} className="border-stone-300 bg-white" data-testid="cir-name" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Category *</label>
          <select value={form.category} onChange={set("category")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="cir-category">
            <option value="">Select…</option>
            {CLIENT_INCIDENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Client's name (if about a client)</label>
          <Input value={form.client_name} onChange={set("client_name")} className="border-stone-300 bg-white" data-testid="cir-client" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Date *</label>
          <Input type="date" value={form.date} onChange={set("date")} max={new Date().toISOString().slice(0, 10)} className="border-stone-300 bg-white" data-testid="cir-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Time</label>
          <Input type="time" value={form.time} onChange={set("time")} className="border-stone-300 bg-white" data-testid="cir-time" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Who was involved / present</label>
        <Input value={form.parties_involved} onChange={set("parties_involved")} className="border-stone-300 bg-white" data-testid="cir-parties" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Witnesses (names, if anyone saw or heard it)</label>
        <Input value={form.witnesses} onChange={set("witnesses")} className="border-stone-300 bg-white" data-testid="cir-witnesses" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">What happened? * <span className="font-normal text-stone-400">(facts only — what you saw and heard, exact words, times)</span></label>
        <Textarea value={form.description} onChange={set("description")} className="min-h-24 border-stone-300 bg-white text-sm" data-testid="cir-description" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">What did you do right away?</label>
        <Textarea value={form.immediate_response} onChange={set("immediate_response")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="cir-response" />
      </div>
      <Button onClick={submit} disabled={busy || !form.category || !form.date || !form.description.trim()}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="cir-submit">
        {busy ? "Filing…" : "File report"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="cir-mine">
          {mine.slice(0, 5).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`cir-mine-${r.id}`}>
              <span>{r.date} · {r.kind} · {r.category}</span>
              <Pill s={r.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminList({ title, rows, actions, onAction, tid, renderLine }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`admin-${tid}`}>
      <p className="font-heading text-sm font-semibold text-stone-900">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-stone-400">Nothing waiting.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border border-stone-100 bg-stone-50 p-3" data-testid={`admin-${tid}-row-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium text-stone-800">{r.employee_name}</span>
                <span className="text-stone-500">{renderLine(r)}</span>
                <Pill s={r.status} />
              </div>
              {(r.description || r.visit_note || r.reason) && (
                <p className="mt-1.5 text-xs text-stone-600">{r.description || r.visit_note || r.reason}</p>
              )}
              {r.tasks && <p className="mt-1 text-[11px] text-stone-500">Tasks: {r.tasks.join(", ")}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {actions.map(([st, label]) => (
                  <button key={st} onClick={() => onAction(r, st)}
                    className="rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] font-semibold text-stone-600 hover:bg-stone-900 hover:text-white"
                    data-testid={`admin-${tid}-${st}-${r.id}`}>{label}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FitDutyForm({ mine, reload }) {
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/fit-duty-agreements", { signature_png: sig });
      toast.success("Fit for Duty Agreement signed");
      setSig(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-accent/60 px-3 py-2 text-xs leading-relaxed text-stone-600" data-testid="ffd-text">
        Per the Fit for Duty & Substance Use Policy: I agree to report to work fit for duty and remain fit while on duty;
        to notify my supervisor before working if any substance or medication may affect my ability to work safely;
        to comply with required testing when supported by law, contract, or documented reasonable suspicion; and I
        understand violations may result in corrective action up to termination.
      </div>
      <p className="text-xs text-stone-500">Signed by <b>{user?.name}</b></p>
      <div>
        <p className="mb-1 text-xs font-medium text-stone-500">Signature *</p>
        <SignaturePad onChange={setSig} />
      </div>
      <Button onClick={submit} disabled={busy || !sig} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="ffd-submit">
        {busy ? "Signing…" : "Sign Fit for Duty Agreement"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="ffd-mine">
          {mine.slice(0, 3).map((r) => (
            <div key={r.id} className="text-xs text-stone-600" data-testid={`ffd-mine-${r.id}`}>Signed {(r.agreed_at || "").slice(0, 10)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccommodationForm({ mine, reload }) {
  const [form, setForm] = useState({ requester_name: "", role: "Employee", request: "", barrier: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.requester_name ? f : { ...f, requester_name: user.name })); }, [user]);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/accommodation-requests", { ...form, signature_png: sig });
      toast.success("Accommodation request sent — the office will review it promptly and confidentially");
      setForm((f) => ({ requester_name: f.requester_name, role: "Employee", request: "", barrier: "" })); setSig(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <p className="rounded-md bg-accent/60 px-3 py-2 text-xs leading-relaxed text-stone-600" data-testid="ada-hint">
        Per the ADA Compliance Policy — reasonable accommodations are available at no cost for applicants, employees, and clients. Requests are reviewed promptly, respectfully, and confidentially.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Your name *</label>
          <Input value={form.requester_name} onChange={set("requester_name")} className="border-stone-300 bg-white" data-testid="ada-name" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">You are a(n)</label>
          <select value={form.role} onChange={set("role")} className="h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="ada-role">
            {["Employee", "Applicant", "Client / participant", "Client representative"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">The accommodation you're requesting *</label>
        <Textarea value={form.request} onChange={set("request")} className="min-h-20 border-stone-300 bg-white text-sm" data-testid="ada-request" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">The disability or barrier it relates to <span className="font-normal text-stone-400">(optional — share only what you're comfortable with)</span></label>
        <Textarea value={form.barrier} onChange={set("barrier")} className="min-h-16 border-stone-300 bg-white text-sm" data-testid="ada-barrier" />
      </div>
      <div>
        <p className="mb-1 text-xs font-medium text-stone-500">Signature *</p>
        <SignaturePad onChange={setSig} />
      </div>
      <Button onClick={submit} disabled={busy || !form.request.trim() || !sig}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="ada-submit">
        {busy ? "Submitting…" : "Submit accommodation request"}
      </Button>
      {mine.length > 0 && (
        <div className="space-y-1.5 border-t border-stone-100 pt-3" data-testid="ada-mine">
          {mine.slice(0, 5).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs text-stone-600" data-testid={`ada-mine-${r.id}`}>
              <span>{(r.submitted_at || "").slice(0, 10)} · {r.request.slice(0, 60)}</span>
              <Pill s={r.status} />
              {r.review_note && <span className="text-stone-400">— {r.review_note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DestructionLogForm({ reload }) {
  const [form, setForm] = useState({ destruction_date: todayStr(), description: "", record_type: "", retention_through: "", method: "", destroyed_by: "", witness: "" });
  const { user } = useAuth();
  useEffect(() => { if (user?.name) setForm((f) => (f.destroyed_by ? f : { ...f, destroyed_by: user.name })); }, [user]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/destruction-logs", form);
      toast.success("Destruction logged");
      setForm((f) => ({ ...f, description: "", record_type: "", retention_through: "", method: "", witness: "" }));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Destruction date *</label>
          <Input type="date" value={form.destruction_date} onChange={set("destruction_date")} className="border-stone-300 bg-white" data-testid="dl-date" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Record type</label>
          <Input value={form.record_type} onChange={set("record_type")} placeholder="e.g. Personnel file, EVV export" className="border-stone-300 bg-white" data-testid="dl-type" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-stone-500">Records destroyed *</label>
        <Textarea value={form.description} onChange={set("description")} placeholder="Describe the records (names/IDs, date ranges, count)" className="min-h-16 border-stone-300 bg-white text-sm" data-testid="dl-description" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Retention period met through</label>
          <Input type="date" value={form.retention_through} onChange={set("retention_through")} className="border-stone-300 bg-white" data-testid="dl-retention" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Destruction method *</label>
          <Input value={form.method} onChange={set("method")} placeholder="e.g. Cross-shredded, secure delete" className="border-stone-300 bg-white" data-testid="dl-method" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Destroyed by</label>
          <Input value={form.destroyed_by} onChange={set("destroyed_by")} className="border-stone-300 bg-white" data-testid="dl-by" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-stone-500">Witness</label>
          <Input value={form.witness} onChange={set("witness")} className="border-stone-300 bg-white" data-testid="dl-witness" />
        </div>
      </div>
      <Button onClick={submit} disabled={busy || !form.description.trim() || !form.method.trim()}
        className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="dl-submit">
        {busy ? "Saving…" : "Log destruction"}
      </Button>
    </div>
  );
}


export default function FormsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [mine, setMine] = useState({ tcr: [], to: [], ei: [], fd: [], ar: [] });
  const [adminData, setAdminData] = useState({ tcr: [], to: [], ei: [], fd: [], ar: [], dl: [] });

  const loadMine = () => {
    api.get("/task-code-reports/mine").then((r) => setMine((m) => ({ ...m, tcr: r.data.reports || [] }))).catch(() => {});
    api.get("/time-off/mine").then((r) => setMine((m) => ({ ...m, to: r.data.requests || [] }))).catch(() => {});
    api.get("/employee-incidents/mine").then((r) => setMine((m) => ({ ...m, ei: r.data.incidents || [] }))).catch(() => {});
    api.get("/incident-reports/mine").then((r) => setMine((m) => ({ ...m, ir: r.data.reports || [] }))).catch(() => {});
    api.get("/fit-duty-agreements/mine").then((r) => setMine((m) => ({ ...m, fd: r.data.agreements || [] }))).catch(() => {});
    api.get("/accommodation-requests/mine").then((r) => setMine((m) => ({ ...m, ar: r.data.requests || [] }))).catch(() => {});
  };
  const loadAdmin = () => {
    api.get("/task-code-reports").then((r) => setAdminData((d) => ({ ...d, tcr: r.data.reports || [] }))).catch(() => {});
    api.get("/time-off").then((r) => setAdminData((d) => ({ ...d, to: r.data.requests || [] }))).catch(() => {});
    api.get("/employee-incidents").then((r) => setAdminData((d) => ({ ...d, ei: r.data.incidents || [] }))).catch(() => {});
    api.get("/fit-duty-agreements").then((r) => setAdminData((d) => ({ ...d, fd: r.data.agreements || [] }))).catch(() => {});
    api.get("/accommodation-requests").then((r) => setAdminData((d) => ({ ...d, ar: r.data.requests || [] }))).catch(() => {});
    api.get("/destruction-logs").then((r) => setAdminData((d) => ({ ...d, dl: r.data.logs || [] }))).catch(() => {});
  };
  useEffect(() => { if (user) { loadMine(); if (isAdmin) loadAdmin(); } }, [user]);

  const act = async (kind, id, status) => {
    try {
      await api.put(`/${kind}/${id}`, { status });
      toast.success("Updated");
      loadAdmin();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="forms-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="forms-title">Employee Forms</h1>
        <p className="mt-1 text-sm text-stone-500">Task code reports, time-off requests, and employee incident reports. Submissions are timestamped and go straight to the office.</p>
      </div>

      {!isAdmin && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="forms-links">
          <Link to="/missed-punch/new" className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm transition-colors hover:border-primary/40" data-testid="forms-link-missed-punch">
            <p className="text-sm font-semibold text-stone-800">Missed Punch Form</p>
            <p className="mt-0.5 text-xs text-stone-400">Didn't clock in or out? Report the shift, times, and task codes here.</p>
          </Link>
          <Link to="/profile" className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm transition-colors hover:border-primary/40" data-testid="forms-link-update-info">
            <p className="text-sm font-semibold text-stone-800">Update my info</p>
            <p className="mt-0.5 text-xs text-stone-400">Address, phone, email, emergency contact — reviewed by the office.</p>
          </Link>
        </div>
      )}

      {isAdmin ? (
        <div className="space-y-4">
          <AdminList title="Time-off requests" tid="to" rows={adminData.to}
            actions={[["approved", "Approve"], ["denied", "Deny"], ["pending", "Reset to pending"]]}
            onAction={(r, st) => act("time-off", r.id, st)}
            renderLine={(r) => `${r.type} · ${r.start_date} → ${r.end_date}`} />
          <AdminList title="Employee incident reports" tid="ei" rows={adminData.ei}
            actions={[["acknowledged", "Acknowledge"], ["closed", "Close"], ["reported", "Reopen"]]}
            onAction={(r, st) => act("employee-incidents", r.id, st)}
            renderLine={(r) => `${r.incident_date} · ${r.type}${r.location ? ` · ${r.location}` : ""}`} />
          <AdminList title="Task code reports" tid="tcr" rows={adminData.tcr}
            actions={[["entered", "Entered in EVV"], ["reviewed", "Reviewed"], ["submitted", "Reopen"]]}
            onAction={(r, st) => act("task-code-reports", r.id, st)}
            renderLine={(r) => `${r.work_date} · ${r.client_name}`} />
          <AdminList title="Fit for Duty agreements" tid="fd" rows={adminData.fd}
            actions={[]}
            onAction={() => {}}
            renderLine={(r) => `${r.employee_name} · signed ${(r.agreed_at || "").slice(0, 10)}`} />
          <AdminList title="Accommodation requests (ADA)" tid="ar" rows={adminData.ar}
            actions={[["in_review", "In review"], ["approved", "Approve"], ["denied", "Deny"]]}
            onAction={(r, st) => act("accommodation-requests", r.id, st)}
            renderLine={(r) => `${r.requester_name} (${r.role}) · ${(r.request || "").slice(0, 80)}`} />
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-destruction-log">
            <p className="mb-1 font-heading text-sm font-semibold text-stone-900">Records Destruction Log</p>
            <p className="mb-3 text-xs text-stone-400">Per the Record Retention Policy — every destruction is logged here and kept for audit review.</p>
            <DestructionLogForm reload={loadAdmin} />
            {adminData.dl.length > 0 && (
              <div className="mt-3 space-y-1.5 border-t border-stone-100 pt-3" data-testid="dl-list">
                {adminData.dl.slice(0, 20).map((r) => (
                  <div key={r.id} className="text-xs text-stone-600" data-testid={`dl-row-${r.id}`}>
                    {r.destruction_date} · {r.record_type || "records"} · {r.description.slice(0, 60)} · {r.method} · by {r.destroyed_by}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-task-codes">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Task Code Reporting</p>
            <TaskCodeForm mine={mine.tcr} reload={loadMine} />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-time-off">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Request Time Off</p>
            <TimeOffForm mine={mine.to} reload={loadMine} />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-client-incident">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Incident or Complaint (client-related)</p>
            <ClientIncidentForm mine={mine.ir || []} reload={loadMine} />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-employee-incident">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Employee Incident Report</p>
            <EmployeeIncidentForm mine={mine.ei} reload={loadMine} />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-fit-duty">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Fit for Duty Agreement</p>
            <FitDutyForm mine={mine.fd} reload={loadMine} />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="form-accommodation">
            <p className="mb-3 font-heading text-sm font-semibold text-stone-900">Reasonable Accommodation Request (ADA)</p>
            <AccommodationForm mine={mine.ar} reload={loadMine} />
          </div>
        </>
      )}
    </div>
  );
}
